import type { Building, PlayerId, TileMap, TurnWorld, Unit } from './types';
import { UNIT_DEFS } from './units';
import { getVisionCost } from './terrain';
import { activeAurasFor, auraBonus } from './auras';

const NEIGHBORS: [number, number][] = [
  [0, -1], [0, 1], [-1, 0], [1, 0],
];

const BUILDING_SIGHT_RANGE = 2;

/** BFS with visionCost budget — returns set of visible tile keys. */
export function computeUnitVision(unit: Unit, map: TileMap, world?: TurnWorld): Set<string> {
  const def = UNIT_DEFS[unit.type];
  // Commander sight aura widens vision when the world is available.
  const sightBonus = world ? auraBonus(activeAurasFor(world, unit), 'sightBonus') : 0;
  return computeVisionFrom(unit.col, unit.row, def.sightRange + sightBonus, def.domain, map);
}

/** Flat Manhattan vision for buildings — radius 2, no visionCost. */
export function computeBuildingVision(building: Building, map: TileMap): Set<string> {
  const result = new Set<string>();
  const r = BUILDING_SIGHT_RANGE;
  for (let dr = -r; dr <= r; dr++) {
    for (let dc = -r; dc <= r; dc++) {
      if (Math.abs(dr) + Math.abs(dc) > r) continue;
      const c = building.col + dc;
      const row = building.row + dr;
      if (c >= 0 && row >= 0 && c < map.width && row < map.height) {
        result.add(`${c},${row}`);
      }
    }
  }
  return result;
}

/** Union of vision from all units and owned buildings for a player. */
export function computePlayerVisible(playerId: PlayerId, world: TurnWorld): Set<string> {
  const result = new Set<string>();
  for (const [, unit] of world.units) {
    if (unit.owner !== playerId) continue;
    for (const key of computeUnitVision(unit, world.map, world)) {
      result.add(key);
    }
  }
  for (const [, building] of world.buildings) {
    if (building.owner !== playerId) continue;
    for (const key of computeBuildingVision(building, world.map)) {
      result.add(key);
    }
  }
  return result;
}

/** Initialize explored sets: HQ positions for all players + starting vision. */
export function initExplored(world: TurnWorld): void {
  // Add all HQ positions to every player's explored set
  for (const [, b] of world.buildings) {
    if (b.type === 'hq') {
      const key = `${b.col},${b.row}`;
      for (const explored of world.explored) {
        explored.add(key);
      }
    }
  }
  // Compute starting vision for each player and add to explored
  for (const player of world.players) {
    const visible = computePlayerVisible(player.id, world);
    updateExplored(player.id, visible, world);
  }
}

/** Merge visible tiles into the player's explored set. */
export function updateExplored(playerId: PlayerId, visible: Set<string>, world: TurnWorld): void {
  const explored = world.explored[playerId];
  for (const key of visible) {
    explored.add(key);
  }
}

// --- Internal ---

// Sentinel for "not visited" in flat best-budget array (-1 means unvisited)
const NOT_VISITED = -1;

// Reusable flat array for BFS best-budget tracking — avoids Map<string,number> per call.
// Grows once to max map size, then is reused across all vision computations.
let bestBuf: Int8Array | null = null;
let bestBufSize = 0;

function ensureBestBuf(size: number): Int8Array {
  if (!bestBuf || bestBufSize < size) {
    bestBufSize = size;
    bestBuf = new Int8Array(size);
  }
  bestBuf.fill(NOT_VISITED, 0, size);
  return bestBuf;
}

function computeVisionFrom(
  col: number, row: number, sightRange: number, domain: string, map: TileMap,
): Set<string> {
  const result = new Set<string>();
  const w = map.width;
  const h = map.height;
  const best = ensureBestBuf(w * h);

  // Dijkstra with bucket queue — budgets are small integers (0..sightRange),
  // so an array of buckets gives O(1) push/pop instead of O(n log n) sort.
  // Buckets indexed by remaining budget (0..sightRange)
  const buckets: [number, number][][] = Array.from({ length: sightRange + 1 }, () => []);

  const startIdx = col * h + row;
  result.add(`${col},${row}`);
  best[startIdx] = sightRange;

  // Push neighbors of starting tile — first step always costs 1
  for (const [dc, dr] of NEIGHBORS) {
    const nc = col + dc;
    const nr = row + dr;
    if (nc < 0 || nr < 0 || nc >= w || nr >= h) continue;
    buckets[sightRange - 1].push([nc, nr]);
  }

  // Process highest budget first
  for (let budget = sightRange - 1; budget >= 0; budget--) {
    const bucket = buckets[budget];
    for (let i = 0; i < bucket.length; i++) {
      const [c, r] = bucket[i];
      const idx = c * h + r;

      if (best[idx] >= budget) continue;
      best[idx] = budget;
      result.add(`${c},${r}`);

      if (budget <= 0) continue;

      for (const [dc, dr] of NEIGHBORS) {
        const nc = c + dc;
        const nr = r + dr;
        if (nc < 0 || nr < 0 || nc >= w || nr >= h) continue;
        const cost = getVisionCost(map.tiles[nr][nc].type, domain as 'land' | 'water' | 'air');
        const remaining = budget - cost;
        if (remaining < 0) continue;
        const nIdx = nc * h + nr;
        if (best[nIdx] >= remaining) continue;
        buckets[remaining].push([nc, nr]);
      }
    }
  }

  return result;
}
